const COINGECKO_BASE_URL =
  process.env.VUE_APP_COINGECKO_BASE_URL || 'https://api.coingecko.com/api/v3'

// Ключ CoinGecko берём из .env/.env.local:
// VUE_APP_COINGECKO_API_KEY=CG-xxxxxxxxxxxxxxxxxxxxxxxx
const COINGECKO_API_KEY = process.env.VUE_APP_COINGECKO_API_KEY || ''

// Простой in-memory кеш для запросов markets (живёт пока открыт таб)
const marketsCache = {
  // key: string -> { timestamp: number, data: any }
  entries: {},
  ttl: 30_000 // 30 секунд
}

// Кеш для иконок монет (символ -> URL иконки)
const coinIconsCache = {
  entries: {},
  ttl: 24 * 60 * 60 * 1000 // 24 часа
}

// Иконки внешних хостов (coingecko/tradingview/ibb) у части клиентов не грузятся —
// эти хосты им блокируются/тормозят. Прогоняем такие ссылки через свой прокси
// /api/coin-icon: он отдаёт иконку с нашего домена (с диска, докачивая при первом
// обращении). Ссылки на свой домен и data:-URI не трогаем.
const PROXIED_ICON_HOSTS = [
  'assets.coingecko.com',
  'coin-images.coingecko.com',
  's3-symbol-logo.tradingview.com',
  'i.ibb.co'
]

export function localIcon(url) {
  if (!url || typeof url !== 'string') return url
  if (!url.startsWith('https://')) return url
  try {
    const host = new URL(url).hostname
    if (!PROXIED_ICON_HOSTS.includes(host)) return url
    return `/api/coin-icon?src=${encodeURIComponent(url)}`
  } catch (e) {
    return url
  }
}

async function request(path, params = {}, { cacheKey } = {}) {
  const url = new URL(COINGECKO_BASE_URL + path)

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value))
    }
  })

  const headers = {}

  if (COINGECKO_API_KEY) {
    // Для Basic/Pro планов используется заголовок x-cg-pro-api-key
    // Для Demo/Free планов используется x-cg-demo-api-key
    headers['x-cg-pro-api-key'] = COINGECKO_API_KEY
  }

  // Проверка кеша только для markets-запросов
  if (cacheKey && marketsCache.entries[cacheKey]) {
    const cached = marketsCache.entries[cacheKey]
    if (Date.now() - cached.timestamp < marketsCache.ttl) {
      return cached.data
    }
  }

  // Добавляем таймаут для запросов (10 секунд)
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 10000)

  let response
  try {
    response = await fetch(url.toString(), { 
      headers,
      signal: controller.signal
    })
  } catch (error) {
    clearTimeout(timeoutId)
    if (error.name === 'AbortError') {
      throw new Error('Request timeout')
    }
    throw error
  }
  clearTimeout(timeoutId)

  if (!response.ok) {
    throw new Error(`CoinGecko request failed: ${response.status}`)
  }

  const data = await response.json()

  if (cacheKey) {
    marketsCache.entries[cacheKey] = {
      timestamp: Date.now(),
      data
    }
  }

  return data
}

// Кеш для текущих цен монет
const currentPriceCache = {
  entries: {},
  ttl: 30_000 // 30 секунд
}

// Кеш для исторических цен
const historicalPriceCache = {
  entries: {},
  ttl: 5 * 60 * 1000 // 5 минут
}

export const marketApi = {
  /**
   * Получить текущую цену монеты по её CoinGecko ID
   * @param {string} coinId - ID монеты на CoinGecko (например: 'bitcoin', 'ethereum', 'solana')
   * @returns {Promise<number|null>} - Текущая цена в USD или null при ошибке
   */
  async getCurrentPrice(coinId) {
    if (!coinId) return null

    const coinIdLower = coinId.toLowerCase().trim()
    const cacheKey = `price_${coinIdLower}`

    // Проверяем кеш
    if (currentPriceCache.entries[cacheKey]) {
      const cached = currentPriceCache.entries[cacheKey]
      if (Date.now() - cached.timestamp < currentPriceCache.ttl) {
        return cached.price
      }
    }

    try {
      const data = await request('/simple/price', {
        ids: coinIdLower,
        vs_currencies: 'usd'
      })

      if (data && data[coinIdLower] && data[coinIdLower].usd !== undefined) {
        const price = data[coinIdLower].usd
        
        // Кешируем результат
        currentPriceCache.entries[cacheKey] = {
          timestamp: Date.now(),
          price: price
        }
        
        return price
      }
      
      return null
    } catch (error) {
      console.warn(`Failed to get current price for ${coinId}:`, error)
      return null
    }
  },

  /**
   * Получить историческую цену монеты на определённую дату
   * @param {string} coinId - ID монеты на CoinGecko
   * @param {Date|string} date - Дата для получения цены
   * @returns {Promise<number|null>} - Цена на указанную дату в USD или null при ошибке
   */
  async getHistoricalPrice(coinId, date) {
    if (!coinId || !date) return null

    const coinIdLower = coinId.toLowerCase().trim()
    const dateObj = date instanceof Date ? date : new Date(date)
    
    if (isNaN(dateObj.getTime())) return null

    // Форматируем дату в формате dd-mm-yyyy для CoinGecko API
    const day = String(dateObj.getDate()).padStart(2, '0')
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const year = dateObj.getFullYear()
    const dateStr = `${day}-${month}-${year}`
    
    const cacheKey = `historical_${coinIdLower}_${dateStr}`

    // Проверяем кеш
    if (historicalPriceCache.entries[cacheKey]) {
      const cached = historicalPriceCache.entries[cacheKey]
      if (Date.now() - cached.timestamp < historicalPriceCache.ttl) {
        return cached.price
      }
    }

    try {
      const data = await request(`/coins/${coinIdLower}/history`, {
        date: dateStr,
        localization: 'false'
      })

      if (data && data.market_data && data.market_data.current_price && 
          data.market_data.current_price.usd !== undefined) {
        const price = data.market_data.current_price.usd
        
        // Кешируем результат
        historicalPriceCache.entries[cacheKey] = {
          timestamp: Date.now(),
          price: price
        }
        
        return price
      }
      
      return null
    } catch (error) {
      console.warn(`Failed to get historical price for ${coinId} on ${dateStr}:`, error)
      return null
    }
  },

  /**
   * Проверить, является ли coin_id валидным (существует на CoinGecko)
   * @param {string} coinId - ID монеты на CoinGecko
   * @returns {Promise<boolean>} - true если монета существует
   */
  async isValidCoinId(coinId) {
    if (!coinId) return false
    
    const price = await this.getCurrentPrice(coinId)
    return price !== null
  },

  /**
   * Получить список рынков (монет) с основными метриками
   */
  async getMarkets(options = {}) {
    const {
      vsCurrency = 'usd',
      perPage = 250,
      page = 1,
      order = 'market_cap_desc',
      sparkline = true,
      ids = null // список id через запятую — цифры по конкретным монетам (для поиска)
    } = options

    const cacheKey = JSON.stringify({
      type: 'markets',
      vsCurrency,
      perPage,
      page,
      order,
      sparkline,
      ids
    })

    return request(
      '/coins/markets',
      {
        vs_currency: vsCurrency,
        order,
        per_page: perPage,
        page,
        price_change_percentage: '24h',
        sparkline: sparkline ? 'true' : 'false',
        ...(ids ? { ids } : {})
      },
      { cacheKey }
    )
  },

  /**
   * Получить детальную информацию по монете
   */
  async getCoinDetails(id) {
    if (!id) throw new Error('Coin id is required')

    return request(`/coins/${encodeURIComponent(id)}`, {
      localization: 'false',
      tickers: 'false',
      market_data: 'true',
      community_data: 'false',
      developer_data: 'false',
      sparkline: 'true'
    })
  },

  /**
   * Получить URL иконки монеты по символу (BTC, ETH, USDT и т.д.)
   */
  async getCoinIcon(symbol) {
    if (!symbol) return null

    const symbolUpper = symbol.toUpperCase()

    // Проверяем кеш
    if (coinIconsCache.entries[symbolUpper]) {
      const cached = coinIconsCache.entries[symbolUpper]
      if (Date.now() - cached.timestamp < coinIconsCache.ttl) {
        return cached.url
      }
    }

    // Кастомные иконки для активов, отсутствующих на CoinGecko (нефть, индексы и т.д.)
    const customIconMap = {
      'UKOIL': 'https://s3-symbol-logo.tradingview.com/crude-oil--big.svg',
      'USOIL': 'https://s3-symbol-logo.tradingview.com/crude-oil--big.svg',
      'BRENT': 'https://s3-symbol-logo.tradingview.com/crude-oil--big.svg',
      'WTI': 'https://s3-symbol-logo.tradingview.com/crude-oil--big.svg',
      'XAUUSD': 'https://s3-symbol-logo.tradingview.com/crude-oil--big.svg',
      'GOLD': 'https://s3-symbol-logo.tradingview.com/crude-oil--big.svg'
    }

    if (customIconMap[symbolUpper]) {
      const iconUrl = localIcon(customIconMap[symbolUpper])
      coinIconsCache.entries[symbolUpper] = {
        timestamp: Date.now(),
        url: iconUrl
      }
      return iconUrl
    }

    try {
      // Используем простой маппинг популярных монет на их CoinGecko ID
      const coinIdMap = {
        'BTC': 'bitcoin',
        'ETH': 'ethereum',
        'USDT': 'tether',
        'BNB': 'binancecoin',
        'SOL': 'solana',
        'XRP': 'ripple',
        'USDC': 'usd-coin',
        'DOGE': 'dogecoin',
        'ADA': 'cardano',
        'TRX': 'tron',
        'AVAX': 'avalanche-2',
        'DOT': 'polkadot',
        'LINK': 'chainlink',
        'MATIC': 'matic-network',
        'UNI': 'uniswap',
        'LTC': 'litecoin',
        'ATOM': 'cosmos',
        'ETC': 'ethereum-classic',
        'XLM': 'stellar',
        'ALGO': 'algorand',
        'VET': 'vechain',
        'ICP': 'internet-computer',
        'FIL': 'filecoin',
        'THETA': 'theta-token',
        'EOS': 'eos',
        'AAVE': 'aave',
        'MKR': 'maker',
        'COMP': 'compound-governance-token',
        'SUSHI': 'sushi',
        'YFI': 'yearn-finance'
      }

      const coinId = coinIdMap[symbolUpper]

      if (coinId) {
        // Получаем данные монеты для получения правильного URL иконки
        try {
          const coinData = await request(`/coins/${coinId}`, {
            localization: 'false',
            tickers: 'false',
            market_data: 'false',
            community_data: 'false',
            developer_data: 'false',
            sparkline: 'false'
          })

          if (coinData && coinData.image) {
            // Используем large для лучшего качества, fallback на small если large нет
            const iconUrl = localIcon(coinData.image.large || coinData.image.small)

            if (iconUrl) {
            // Кешируем результат
            coinIconsCache.entries[symbolUpper] = {
              timestamp: Date.now(),
              url: iconUrl
            }

            return iconUrl
            }
          }
        } catch (coinError) {
          console.warn(`Failed to get coin data for ${symbolUpper}:`, coinError)
        }
      }

      // Если монета не найдена в маппинге, пытаемся найти через API поиска
      try {
        const searchResult = await request('/search', {
          query: symbolUpper
        })

        if (searchResult && searchResult.coins && searchResult.coins.length > 0) {
          const coin = searchResult.coins[0]
          // Используем large для лучшего качества, fallback на thumb или small
          const iconUrl = localIcon(coin.large || coin.thumb || coin.small || null)

          if (iconUrl) {
            coinIconsCache.entries[symbolUpper] = {
              timestamp: Date.now(),
              url: iconUrl
            }
            return iconUrl
          }
        }
      } catch (searchError) {
        console.warn(`Failed to search coin icon for ${symbolUpper}:`, searchError)
      }

      return null
    } catch (error) {
      console.error(`Error getting coin icon for ${symbolUpper}:`, error)
      return null
    }
  }
}


